// ====================================
// DYNAMIC EXPORT FUNCTIONS
// ====================================

import * as XLSX from 'xlsx';
import { httpClient } from '../../../infra/axios/axios-api';
import { 
  DynamicExportConfig, 
  DynamicReportRequest, 
  WhereCondition, 
  ExportConfig, 
  ExportData, 
  ExportColumn,
  MultiLineExportConfig,
  TableInfo 
} from './types';
import { getFullText, getTimestamp, formatTableDisplayName, formatColumnDisplayName } from './common';

/**
 * Busca tabelas disponíveis para relatórios
 */
export const fetchAvailableTables = async (): Promise<TableInfo[]> => {
  try {
    const response = await httpClient.get('/report/tables');
    const apiData = response.data;
    
    if (apiData && apiData.success && Array.isArray(apiData.data)) {
      return apiData.data.map((tableName: string) => ({
        id: tableName.toLowerCase(),
        name: tableName,
        displayName: formatTableDisplayName(tableName)
      }));
    }
    
    if (apiData && typeof apiData === 'object' && 'data' in apiData && Array.isArray(apiData.data)) {
      if (apiData.data.length > 0 && typeof apiData.data[0] === 'object' && 'id' in apiData.data[0]) {
        return apiData.data;
      }
      return apiData.data.map((tableName: string) => ({
        id: tableName.toLowerCase(),
        name: tableName,
        displayName: formatTableDisplayName(tableName)
      }));
    }
    
    if (Array.isArray(apiData)) {
      return apiData.map((tableName: string) => ({
        id: tableName.toLowerCase(),
        name: tableName,
        displayName: formatTableDisplayName(tableName)
      }));
    }
    
    console.warn('Estrutura de resposta inesperada:', apiData);
    return [];
  } catch (error) {
    console.error(' Erro ao buscar tabelas disponíveis:', error);
    throw new Error('Erro ao carregar lista de tabelas');
  }
};

/**
 * Busca dados de tabelas da API para relatórios dinâmicos
 */
export const fetchReportData = async (request: DynamicReportRequest): Promise<any[]> => {
  try {
    const requestBody = { data: request };
    const response = await httpClient.post('/report/data', requestBody);
    const apiData = response.data;
    let resultData: any[] = [];
    
    if (apiData && apiData.success && Array.isArray(apiData.data)) {
      resultData = apiData.data;
    }
    else if (apiData && typeof apiData === 'object' && 'data' in apiData) {
      resultData = Array.isArray(apiData.data) ? apiData.data : [];
    }
    else if (Array.isArray(apiData)) {
      resultData = apiData;
    }
    else {
      console.warn(' Estrutura de resposta inesperada para dados:', apiData);
      resultData = [];
    }
    
    if (resultData.length > 0) {
      return resultData;
    } else {
      throw new Error(`API retornou dados vazios para a tabela ${request.table}`);
    }
    
  } catch (error) {
    console.error(' Erro ao buscar dados do relatório:', error);
    console.error(' Detalhes do erro:', (error as any)?.response?.data || (error as any)?.message);
    throw new Error(`Falha ao buscar dados da tabela ${request.table}: ${(error as any)?.message || 'Erro desconhecido'}`);
  }
};

/**
 * Busca as colunas disponíveis para uma tabela
 */
export const fetchTableColumns = async (tableName: string): Promise<any[]> => {
  try {
    const response = await httpClient.get(`/report/columns?tableName=${tableName}`);
    const apiResponse = response.data;
    
    if (apiResponse && apiResponse.success && Array.isArray(apiResponse.data)) {
      return apiResponse.data.map((col: any) => {
        if (typeof col === 'string') {
          return {
            name: col,
            type: 'string', // Default fallback type
            displayName: formatColumnDisplayName(col, tableName)
          };
        }
        return col;
      });
    }
    
    if (Array.isArray(apiResponse)) {
      return apiResponse.map((col: any) => {
        if (typeof col === 'string') {
          return {
            name: col,
            type: 'string',
            displayName: formatColumnDisplayName(col, tableName)
          };
        }
        return col;
      });
    }
    
    throw new Error(`API não retornou colunas para a tabela ${tableName}`);
    
  } catch (error) {
    console.error(` Erro ao buscar colunas da tabela ${tableName}:`, error);
    throw new Error(`Falha ao buscar colunas da tabela ${tableName}: ${(error as any)?.message || 'Erro desconhecido'}`);
  }
};

/**
 * Exportação dinâmica para Excel
 */
export const exportDynamicToExcel = async (config: DynamicExportConfig): Promise<void> => {
  try {
    const reportRequest: DynamicReportRequest = {
      table: config.tableInfo.name,
      columns: config.selectedColumns.map(col => col.name),
      where: config.whereConditions
    };

    const data = await fetchReportData(reportRequest);

    if (data.length === 0) {
      throw new Error('Nenhum dado encontrado com os filtros aplicados');
    }

    const excelData = data.map(item => {
      const row: { [key: string]: any } = {};
      config.selectedColumns.forEach(col => {
        const displayName = col.displayName || formatColumnDisplayName(col.name, config.tableInfo.name);
        row[displayName] = getFullText(item[col.name]);
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    
    const sheetName = config.tableInfo.displayName || formatTableDisplayName(config.tableInfo.name);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    const filename = `${config.filename}_${getTimestamp().replace(/[/:]/g, '-')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error(' Erro na exportação dinâmica Excel:', error);
    throw error;
  }
};

/**
 * Exportação dinâmica para PDF
 */
export const exportDynamicToPdf = async (config: DynamicExportConfig): Promise<void> => {
  try {
    const reportRequest: DynamicReportRequest = {
      table: config.tableInfo.name,
      columns: config.selectedColumns.map(col => col.name),
      where: config.whereConditions
    };

    const data = await fetchReportData(reportRequest);

    if (data.length === 0) {
      throw new Error('Nenhum dado encontrado com os filtros aplicados');
    }

    const [jsPDFModule, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    
    const jsPDF = (jsPDFModule as any).default || jsPDFModule;
    const autoTable = (autoTableModule as any).default || autoTableModule;
    
    const doc = new jsPDF({
      orientation: config.orientation || 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const titleFontSize = config.fontSize?.title || 14;
    const headerFontSize = config.fontSize?.header || 8;
    const bodyFontSize = config.fontSize?.body || 7;

    // Título
    doc.setFontSize(titleFontSize);
    doc.setTextColor(33, 150, 243);
    doc.text(config.title, 15, 20);

    // Subtítulo
    if (config.subtitle) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(config.subtitle, 15, 28);
    }

    // Informações da consulta
    const tableDisplayName = config.tableInfo.displayName || formatTableDisplayName(config.tableInfo.name);
    const queryInfo = `Tabela: ${tableDisplayName} | Colunas: ${config.selectedColumns.length} | Registros: ${data.length}`;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(queryInfo, 15, config.subtitle ? 34 : 28);

    // Timestamp
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Exportado em: ${getTimestamp()}`, 15, config.subtitle ? 40 : 34);

    doc.setTextColor(0, 0, 0);

    // Preparar colunas
    const columns = config.selectedColumns.map(col => ({
      header: col.displayName || formatColumnDisplayName(col.name, config.tableInfo.name),
      dataKey: col.name
    }));

    // Preparar dados
    const tableData = data.map(item => {
      const row: { [key: string]: any } = {};
      config.selectedColumns.forEach(col => {
        row[col.name] = getFullText(item[col.name]);
      });
      return row;
    });

    // Gerar tabela
    autoTable(doc, {
      columns: columns,
      body: tableData,
      startY: config.subtitle ? 46 : 40,
      theme: 'striped',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: [255, 255, 255],
        fontSize: headerFontSize,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        minCellHeight: 10,
        overflow: 'linebreak'
      },
      bodyStyles: {
        fontSize: bodyFontSize,
        cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
        overflow: 'linebreak',
        minCellHeight: 8,
        valign: 'top',
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: {
        left: 5,
        right: 5,
        bottom: 25
      },
      tableWidth: 'auto',
      showHead: 'everyPage',
      didDrawPage: function (data: any) {
        if (config.showPageNumbers) {
          const pageNumber = data.pageNumber;
          const totalPages = (doc as any).internal?.getNumberOfPages?.() || '?';
          
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Página ${pageNumber} de ${totalPages}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10
          );
        }
      }
    });

    const filename = `${config.filename}_${getTimestamp().replace(/[/:]/g, '-')}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error(' Erro na exportação dinâmica PDF:', error);
    throw error;
  }
};

/**
 * Exportação simples para Excel
 */
export const exportToExcel = (data: ExportData[], config: ExportConfig): void => {
  try {
    const excelData = data.map(item => {
      const row: { [key: string]: any } = {};
      config.columns.forEach(col => {
        row[col.header] = getFullText(item[col.dataKey]);
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
    
    const filename = `${config.filename}_${getTimestamp().replace(/[/:]/g, '-')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error(' Erro na exportação Excel:', error);
    throw new Error('Erro ao exportar para Excel');
  }
};

/**
 * Exportação simples para PDF
 */
export const exportToPdf = async (data: ExportData[], config: ExportConfig): Promise<void> => {
  try {
    const [jsPDFModule, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    
    const jsPDF = (jsPDFModule as any).default || jsPDFModule;
    const autoTable = (autoTableModule as any).default || autoTableModule;
    
    const doc = new jsPDF({
      orientation: config.orientation || 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const titleFontSize = config.fontSize?.title || 14;
    const headerFontSize = config.fontSize?.header || 8;
    const bodyFontSize = config.fontSize?.body || 7;

    doc.setFontSize(titleFontSize);
    doc.setTextColor(33, 150, 243);
    doc.text(config.title, 15, 20);

    if (config.subtitle) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(config.subtitle, 15, 28);
    }

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Exportado em: ${getTimestamp()}`, 15, config.subtitle ? 34 : 28);

    doc.setTextColor(0, 0, 0);
    const columns = config.columns.map(col => ({
      header: col.header,
      dataKey: col.dataKey
    }));

    const tableData = data.map(item => {
      const row: { [key: string]: any } = {};
      config.columns.forEach(col => {
        row[col.dataKey] = getFullText(item[col.dataKey]);
      });
      return row;
    });

    autoTable(doc, {
      columns: columns,
      body: tableData,
      startY: config.subtitle ? 40 : 35,
      theme: 'striped',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: [255, 255, 255],
        fontSize: headerFontSize,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        minCellHeight: 10,
        overflow: 'linebreak'
      },
      bodyStyles: {
        fontSize: bodyFontSize,
        cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
        overflow: 'linebreak',
        minCellHeight: 8,
        valign: 'top',
        lineColor: [200, 200, 200],
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: {
        left: 5,
        right: 5,
        bottom: 25
      },
      tableWidth: 'auto',
      showHead: 'everyPage',
      didDrawPage: function (data: any) {
        if (config.showPageNumbers) {
          const pageNumber = data.pageNumber;
          const totalPages = (doc as any).internal?.getNumberOfPages?.() || '?';
          
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(
            `Página ${pageNumber} de ${totalPages}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10
          );
        }
      }
    });

    const filename = `${config.filename}_${getTimestamp().replace(/[/:]/g, '-')}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error(' Erro na exportação PDF:', error);
    throw new Error('Erro ao exportar para PDF');
  }
};

/**
 * Utilitários de validação e formatação
 */
export const formatOperatorDisplay = (operator: WhereCondition['operator']): string => {
  const operatorMap: { [key: string]: string } = {
    'equals': 'Igual a',
    'not equals': 'Diferente de',
    'contains': 'Contém',
    'not contains': 'Não contém',
    'greater than': 'Maior que',
    'less than': 'Menor que',
    'greater than or equal': 'Maior ou igual a',
    'less than or equal': 'Menor ou igual a',
    'is null': 'É nulo',
    'is not null': 'Não é nulo'
  };
  
  return operatorMap[operator] || operator;
};

export const validateDynamicExportConfig = (config: DynamicExportConfig): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!config.tableInfo?.name) {
    errors.push('Tabela deve ser selecionada');
  }

  if (!config.selectedColumns || config.selectedColumns.length === 0) {
    errors.push('Pelo menos uma coluna deve ser selecionada');
  }

  if (!config.filename?.trim()) {
    errors.push('Nome do arquivo é obrigatório');
  }

  if (!config.title?.trim()) {
    errors.push('Título do relatório é obrigatório');
  }

  if (config.whereConditions) {
    config.whereConditions.forEach((condition, index) => {
      if (!condition.column) {
        errors.push(`Condição ${index + 1}: Coluna é obrigatória`);
      }
      if (!condition.operator) {
        errors.push(`Condição ${index + 1}: Operador é obrigatório`);
      }
      if (condition.operator !== 'is null' && condition.operator !== 'is not null' && (condition.value === null || condition.value === undefined || condition.value === '')) {
        errors.push(`Condição ${index + 1}: Valor é obrigatório para este operador`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
};



/**
 * Exportação PDF multi-linha (para casos especiais)
 */
export const exportMultiLineToPdf = async (
  data: ExportData[],
  config: MultiLineExportConfig
): Promise<void> => {
  try {
    const [jsPDFModule, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    
    const jsPDF = (jsPDFModule as any).default || jsPDFModule;
    const autoTable = (autoTableModule as any).default || autoTableModule;
    
    const doc = new jsPDF({
      orientation: config.orientation || 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const titleFontSize = config.fontSize?.title || 14;
    
    doc.setFontSize(titleFontSize);
    doc.setTextColor(33, 150, 243);
    doc.text(config.title, 15, 20);

    if (config.subtitle) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(config.subtitle, 15, 28);
    }

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Exportado em: ${getTimestamp()}`, 15, config.subtitle ? 34 : 28);

    doc.setTextColor(0, 0, 0);

    let currentY = config.subtitle ? 45 : 40;
    const pageHeight = doc.internal.pageSize.height;
    
    data.forEach((item, index) => {
      if (currentY > pageHeight - 100) {
        doc.addPage();
        currentY = 20;
      }
      
      const columns = config.columns.map(col => ({
        header: col.header,
        dataKey: col.dataKey
      }));
      
      const tableData = [{
        [config.columns[0].dataKey]: getFullText(item[config.columns[0].dataKey]),
        [config.columns[1].dataKey]: getFullText(item[config.columns[1].dataKey]),
        [config.columns[2].dataKey]: getFullText(item[config.columns[2].dataKey])
      }];

      autoTable(doc, {
        columns: columns,
        body: tableData,
        startY: currentY,
        theme: 'grid',
        headStyles: {
          fillColor: [33, 150, 243],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          minCellHeight: 8
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
          overflow: 'linebreak',
          valign: 'top',
          lineColor: [200, 200, 200],
          lineWidth: 0.5
        },
        columnStyles: {
          0: { cellWidth: 90 },
          1: { cellWidth: 90 }, 
          2: { cellWidth: 90 }
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto',
        showHead: 'firstPage'
      });
      
      currentY = (doc as any).lastAutoTable.finalY + 15;
    });

    const filename = `${config.filename}_${getTimestamp().replace(/[/:]/g, '-')}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error(' Erro na exportação PDF multi-linha:', error);
    throw new Error('Erro ao exportar PDF multi-linha');
  }
};